import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Popup } from "@/Common/Components/Modals/Popup";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { ItemBlockingDeleteAction } from "@/Patient/Models/MedicalSummary/Encounter";
import { EncounterItemTypeEnum } from "@/Patient/Models/MedicalSummary/EncounterItemTypeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";

interface IMedicalNoteErrorWarningPopupProps {
    noteModel: IPatientMedicalNoteModel;
}

class MedicalNoteErrorWarningPopup extends BaseReactComponent<IMedicalNoteErrorWarningPopupProps> {
    private id: string = "MedicalNoteErrorWarningPopup";

    public constructor(props: IMedicalNoteErrorWarningPopupProps) {
        super(props);
        super.setOverlay(this.id);
    }

    public render(): JSX.Element {
        const noteModel = this.props.noteModel;
        const permanentBlockingDeleteItems = (noteModel && noteModel.get("PermanentBlockingDeleteItems")) || [];
        const temporaryBlockingDeleteItems = this.groupMultipleEntryAndSetCount(noteModel && noteModel.get("TemporaryBlockingDeleteItems")) || [];

        const showPermanentBlockingDeleteItems = permanentBlockingDeleteItems.any();
        const showTemporaryBlockingDeleteItems = temporaryBlockingDeleteItems.any();

        return (
            <Popup
                Id={this.id}
                title={cacheStore.GetString("WarningLabel")}
                closeTarget={LocalPatientOperations.MedicalSummary.CloseMedicalItemWarningPopup}
                width={550}
                draggable
                centeredHorizontally
            >
                <div className="p-body">
                    {(!showPermanentBlockingDeleteItems && !showTemporaryBlockingDeleteItems) && (
                        <div>
                            <span>{cacheStore.GetString("EncounterActionErrorLabel")}</span>
                        </div>
                    )}
                    {showPermanentBlockingDeleteItems && (
                        <div>
                            <span>{cacheStore.GetString("PermanentBlockingItemsLabel")}</span>
                            <div className="encounter-action-item-popup">
                                <table style={{ cursor: "default" }}>
                                    <tbody>
                                        {permanentBlockingDeleteItems.map(item => {
                                            const date = item.ItemDate;
                                            const name = item.Name || "";
                                            const itemTypeLabel = ItemBlockingDeleteAction.getEncounterItemTypeLabel(item.Type);

                                            return (
                                                <tr key={item.ItemId} title={`${itemTypeLabel} (${moment(date).format("YYYY-MM-DD")}) : ${name}`}>
                                                    <td className="c1">
                                                        <strong>{itemTypeLabel}</strong>
                                                    </td>

                                                    <td className="c2" style={{ minWidth: "100px" }}>
                                                        <span style={{ fontStyle: "italic" }}> {`(${moment(date).format("YYYY-MM-DD")})`} </span>
                                                        {!IsNullOrWhiteSpace(name) && <span> : </span>}
                                                    </td>

                                                    <td className="c3">
                                                        {item.Type == EncounterItemTypeEnum.PrivateBill ? "#" + limitTextLength(name, 30) : limitTextLength(name, 30)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {showTemporaryBlockingDeleteItems && (
                        <div>
                            <span>{cacheStore.GetString("TemporaryBlockingItemsLabel")}</span>
                            <div className="encounter-action-item-popup">
                                <table style={{ cursor: "default" }}>
                                    <tbody>
                                        {temporaryBlockingDeleteItems.map(item => {
                                            const date = item.ItemDate;
                                            let name = item.Name || "";
                                            const medicalItemTypeLabel = item.MedicalItemType == MedicalItemTypeEnum.PastAndOngoingProblemsAndConditions
                                                ? MedicalItemModel.getMedicalItemSubTypeLabel(item.MedicalItemSubType)
                                                : MedicalItemModel.getMedicalItemTypeLabel(item.MedicalItemType);
                                            const itemTypeLabel = ItemBlockingDeleteAction.getEncounterItemTypeLabel(item.Type);

                                            if (item.MedicalItemType == MedicalItemTypeEnum.ScreeningTest && item.Name != null && !isNaN(+item.Name)) {
                                                const testDefinition = cacheStore.getTestDefinitionById(+item.Name);
                                                name = (testDefinition && testDefinition.getTranslatedTestDefinitionName()) || "";
                                            }

                                            return (
                                                <tr key={item.ItemId} title={`${medicalItemTypeLabel || itemTypeLabel} (${moment(date).format("YYYY-MM-DD")}) : ${name}`}>
                                                    <td className="c1">
                                                        <strong>{medicalItemTypeLabel || itemTypeLabel}</strong>
                                                    </td>

                                                    <td className="c2" style={{ minWidth: "100px" }}>
                                                        <span style={{ fontStyle: "italic" }}> {`(${moment(date).format("YYYY-MM-DD")})`} </span>
                                                        {!IsNullOrWhiteSpace(name) && <span> : </span>}
                                                    </td>

                                                    <td className="c3">
                                                        {item.Type == EncounterItemTypeEnum.PrivateBill ? "#" + limitTextLength(name, 30) : limitTextLength(name, 30)}
                                                        {item.EntryCount > 1 && <span> {`( ${item.EntryCount} ${cacheStore.GetString("EntriesLabel")})`}</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
                <div className="p-footer">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                            <div>
                                <div>
                                    <button className="btn btn-primary" disabled={false} onClick={() => this.close()}>{cacheStore.GetString("OKLabel")}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }

    private close(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalSummary.CloseMedicalItemWarningPopup);
    }

    private groupMultipleEntryAndSetCount(items: ItemBlockingDeleteAction[]): ItemBlockingDeleteAction[] {
        if (!items)
            return items;

        return items
            .groupBy(x => x.Name)
            .map(group => {
                const firstItem = group.firstOrDefault();
                firstItem.EntryCount = group.length;
                return firstItem;
            });
    }
}

export { MedicalNoteErrorWarningPopup };
